import { useState, useEffect, useRef, useMemo } from "react"
import styled from "styled-components/macro"
import gsap from "gsap"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"
import MenuButton from "@common/ui/MenuButton"
import useMaskGSAP from "@common/hooks/useMaskGSAP"
import Logo from "@common/ui/Logo"
import ListItem from "@common/ui/ListItem"
import { useProjectDataStore } from "@state/projectDataStore"
import { getColor } from "@utils/getColor"

const windowConfig = {
  Home: {
    width: "70%",
    height: "50%",
    y: "25%",
    x: "30%",
    transform: "",
  },
  Gallery: {
    width: "60%",
    height: "50%",
    y: "25%",
    x: "35%",
    transform: "",
  },
  Residences: {
    width: "80%",
    height: "50%",
    y: "25%",
    x: "70%",
    transform: "",
  },
  Map: {
    width: "20%",
    height: "50%",
    y: "25%",
    x: "70%",
    transform: "",
  },
}

interface Route {
  navLabel: string
  pageType: string
  include: string
  id: string
}

interface Branding {
  navAlign?: "left" | "right"
  navShapeSize?: string
  colors?: {
    background?: string
    primary?: string
  }
}

function WindowNav() {
  const { brandingData, navigationData } = useProjectDataStore((s) => ({
    brandingData: s.brandingData,
    navigationData: s.navigationData,
  }))

  const routes =
    navigationData?.menuItems.filter((item) => item.include === "true") || []

  const [open, setOpen] = useState(false)
  const menuRef = useRef<HTMLElement>(null)
  const initialRoute = useRef<string>()

  const [searchParams] = useSearchParams()

  useEffect(() => {
    const openNavParam = searchParams.get("openNav")
    if (openNavParam !== null) {
      setTimeout(() => setOpen(true), 500)
    }
  }, [searchParams])

  const { onPointerIn, onPointerOut, reveal, SVGMask } = useMemo(
    () =>
      useMaskGSAP({
        targetRef: menuRef,
      }),
    [menuRef.current],
  )

  const navigate = useNavigate()

  useEffect(() => {
    if (!menuRef.current) return
    gsap.to(menuRef.current, {
      autoAlpha: open ? 1 : 0,
      delay: open ? 0 : 0.2,
    })
  }, [open])

  const align = brandingData?.navAlign || "left"
  const shapeSize = brandingData?.navShapeSize || "min(10vh, 10vw)"
  const logoWidth = navigationData?.logoWidth || 12.25
  const navLinkFontColour = getColor(navigationData?.fontColour)
  const shapeColour = getColor(navigationData?.shapeColour)

  const { search } = useLocation()

  return (
    <>
      <Nav
        $bgColor={getColor(navigationData?.bgColor)}
        $shapeSize={shapeSize}
        $align={align}
        ref={menuRef}
      >
        <LinksWrapper onPointerLeave={onPointerOut}>
          {routes
            ?.filter(
              (route) => route?.include === "true" && route?.path !== "splash",
            )
            ?.map(({ navLabel, pageType }) => {
              return (
                <ListItem
                  brandingData={brandingData}
                  fontSize={5}
                  fontColour={navLinkFontColour}
                  key={`${pageType}`}
                  onPointerUp={() => {
                    navigate(`${pageType}${search}`)
                    reveal("default", () => {
                      gsap.set(menuRef.current, {
                        autoAlpha: 0,
                      })
                      setOpen(false)
                    })
                  }}
                >
                  {navLabel}
                </ListItem>
              )
            })}
        </LinksWrapper>
        {navigationData?.shapeColour !== "none" && (
          <>
            {
              <svg
                width="105"
                height="91"
                viewBox="0 0 105 91"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ position: "absolute", bottom: "0", left: "10%" }}
              >
                <path
                  d="M104.377 91H0.143066L53.4202 0.579091L104.377 91Z"
                  fill={shapeColour}
                />
              </svg>
            }
            {
              <svg
                width="105"
                height="91"
                viewBox="0 0 105 91"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ position: "absolute", top: "0", right: "10%" }}
              >
                <path
                  d="M104.377 0H0.143066L53.4202 90.4209L104.377 0Z"
                  fill={shapeColour}
                />
              </svg>
            }
          </>
        )}
      </Nav>
      <Icons $shapeSize={shapeSize} $align={align}>
        <Logo
          width={logoWidth}
          onPointerUp={() => {
            navigate("/")
            setOpen(false)
          }}
          style={{
            zIndex: 10,
            pointerEvents: "auto",
          }}
        />
        <MenuButton
          brandingData={brandingData}
          navigationData={navigationData}
          onClick={() => {
            if (open) {
              navigate(initialRoute.current)
              setOpen(false)
            } else {
              initialRoute.current = window.location.pathname
              setOpen(true)
            }
          }}
          animationCue={open}
        />
      </Icons>

      <SVGMask />
    </>
  )
}

export default WindowNav

type TNavStyledProps = {
  $bgColor?: string
  $shapeSize: string
  // $shapeColor?: string
  $align: "left" | "right"
}

const Nav = styled.nav<TNavStyledProps>`
  background-color: ${(p) => p.$bgColor || "var(--primary)"};
  --shapeSize: ${(p) => p.$shapeSize};
  --align: ${(p) => p.$align};

  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  visibility: hidden;
  opacity: 0;
`

const Icons = styled.div<{ $shapeSize: string; $align: "left" | "right" }>`
  position: fixed;
  top: 0;
  left: 0;
  display: flex;
  gap: 2rem;
  flex-direction: ${(p) => (p.$align === "left" ? "row" : "row-reverse")};
  align-items: center;
  justify-content: center;
  margin: 2rem;
  pointer-events: none;
`

const LinksWrapper = styled.div`
  position: absolute;
  width: 65%;
  left: 17.5%;
  top: 10%;
  height: 80%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 1.5rem;

  > * {
    pointer-events: all;
  }
`
